import { useEffect, useState, type ReactNode } from "react";
import { clsx } from "./Primitives";

export interface FormField {
  key: string;
  label: string;
  /** "people": pick any number of options; the value is their ids joined by commas. */
  type?: "text" | "textarea" | "number" | "date" | "select" | "people";
  options?: { value: string; label: string }[];
  /** For selects: allow typing a value that isn't in the list ("+ New …"). */
  allowNew?: string;
  placeholder?: string;
  wide?: boolean;
  required?: boolean;
}

export type FormValues = Record<string, string>;

/** NEW_PREFIX marks a select value the user typed in rather than picked. */
export const NEW_PREFIX = "__new__:";

export default function FormDialog({
  title, sub, fields, initial, onSave, onClose, onDelete, deleteLabel = "Delete", saveLabel = "Save", children,
}: {
  title: string;
  sub?: string;
  fields: FormField[];
  initial: FormValues;
  onSave: (v: FormValues) => Promise<void>;
  onClose: () => void;
  onDelete?: () => Promise<void>;
  deleteLabel?: string;
  saveLabel?: string;
  children?: ReactNode;
}) {
  const [v, setV] = useState<FormValues>(initial);
  const [newMode, setNewMode] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(fields.filter(f => f.allowNew && (initial[f.key] || "").startsWith(NEW_PREFIX)).map(f => [f.key, true])));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const missing = fields.some(f => f.required && !(v[f.key] || "").replace(NEW_PREFIX, "").trim());
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(null);
    try { await fn(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  const control = (f: FormField) => {
    const val = v[f.key] ?? "";
    const set = (x: string) => setV(prev => ({ ...prev, [f.key]: x }));
    if (f.type === "people") {
      const on = val.split(",").filter(Boolean);
      return (
        <span className="fd-people">
          {(f.options || []).map(o => {
            const sel = on.includes(o.value);
            return (
              <button type="button" key={o.value} className={clsx("fd-person", sel && "on")} aria-pressed={sel}
                onClick={() => set((sel ? on.filter(x => x !== o.value) : [...on, o.value]).join(","))}>
                {o.label}
              </button>
            );
          })}
        </span>
      );
    }
    if (f.type === "textarea") return <textarea rows={4} value={val} placeholder={f.placeholder} onChange={e => set(e.target.value)} />;
    if (f.type === "select") {
      if (newMode[f.key]) {
        return (
          <span className="fd-new">
            <input autoFocus value={val.replace(NEW_PREFIX, "")} placeholder={f.allowNew}
              onChange={e => set(NEW_PREFIX + e.target.value)} />
            <button type="button" className="btn ghost sm" onClick={() => { setNewMode(m => ({ ...m, [f.key]: false })); set(initial[f.key] || ""); }}>Pick</button>
          </span>
        );
      }
      const opts = f.options || [];
      const withCur = val && !opts.some(o => o.value === val) ? [{ value: val, label: val }, ...opts] : opts;
      return (
        <select value={val} onChange={e => {
          if (e.target.value === "__add") { setNewMode(m => ({ ...m, [f.key]: true })); set(NEW_PREFIX); return; }
          set(e.target.value);
        }}>
          {!f.required && <option value="">—</option>}
          {withCur.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          {f.allowNew && <option value="__add">+ {f.allowNew}</option>}
        </select>
      );
    }
    return <input type={f.type || "text"} step="any" value={val} placeholder={f.placeholder} onChange={e => set(e.target.value)} />;
  };

  return (
    <div className="fd-scrim" onMouseDown={onClose}>
      <div className="fd-panel" role="dialog" aria-label={title} onMouseDown={e => e.stopPropagation()}>
        <div className="fd-head">
          <div>
            <div className="fd-title">{title}</div>
            {sub && <div className="fd-sub">{sub}</div>}
          </div>
          <button className="kbd-chip fd-esc" onClick={onClose}>esc</button>
        </div>
        <div className="fd-body">
          <div className="set-grid">
            {fields.map(f => (
              <label key={f.key} className={clsx("set-field", (f.wide || f.type === "textarea" || f.type === "people") && "wide")}>
                <span className="set-label">{f.label}{f.required && " *"}</span>
                {control(f)}
              </label>
            ))}
          </div>
          {children}
          {err && <div className="set-notice err fd-err">{err}</div>}
        </div>
        <div className="fd-foot">
          {onDelete && (
            <button className="btn ghost sm set-danger" disabled={busy} onClick={() => run(onDelete)}>{deleteLabel}</button>
          )}
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={onClose}>Cancel</button>
          <button className="btn primary sm" disabled={busy || missing} onClick={() => run(() => onSave(v))}>{saveLabel}</button>
        </div>
      </div>
    </div>
  );
}
